import { isNilOrEmpty, pickLatestVersion, type Transaction, TransactionLinkType } from '@rentguru/commons-utils';
import isNil from 'lodash/isNil';
import union from 'lodash/union';

interface VersionedTransaction {
  id: string;
  _version?: number;
  _deleted?: boolean | null;
}

export interface TransactionsFetchRequest {
  shouldFetch: boolean;
  transactionIdsToFetched?: string[];
}

/**
 * Upserts the transactions fetched by id, keeping the higher `_version` of each row and dropping the deleted ones.
 */
export const mergeTransactionsFetchedById = <T extends VersionedTransaction>(
  currentTransactions: T[] | null,
  fetchedTransactions: T[]
): T[] => {
  const fetchedTransactionsById = new Map(fetchedTransactions.map((transaction) => [transaction.id, transaction]));
  const currentIds = new Set((currentTransactions ?? []).map((transaction) => transaction.id));
  const mergedTransactions = (currentTransactions ?? []).map((currentTransaction) => {
    const fetchedTransaction = fetchedTransactionsById.get(currentTransaction.id);
    return isNil(fetchedTransaction) ? currentTransaction : pickLatestVersion(currentTransaction, fetchedTransaction);
  });
  const newTransactions = fetchedTransactions.filter((transaction) => !currentIds.has(transaction.id));
  return [...mergedTransactions, ...newTransactions].filter((transaction) => transaction._deleted !== true);
};

/**
 * Adds a fetch request to the pending one: ids are accumulated, and a request without ids asks for everything.
 */
export const addTransactionsFetchRequest = (
  pendingRequest: TransactionsFetchRequest,
  transactionIds?: string[]
): TransactionsFetchRequest => {
  if (!pendingRequest.shouldFetch) {
    return { shouldFetch: true, transactionIdsToFetched: transactionIds };
  }
  if (isNil(pendingRequest.transactionIdsToFetched) || isNil(transactionIds)) {
    return { shouldFetch: true };
  }
  return { shouldFetch: true, transactionIdsToFetched: union(pendingRequest.transactionIdsToFetched, transactionIds) };
};

type LinkedTransaction = Pick<Transaction, 'links'>;

export const filterTransactionsByRightsAndLeases = <T extends LinkedTransaction>(
  transactions: T[],
  rootUser: boolean,
  financialTransactionsRead: boolean,
  visibleLeaseIds: ReadonlySet<string>
): T[] => {
  if (rootUser) {
    return transactions;
  }
  return transactions.filter((transaction) => {
    if (isNilOrEmpty(transaction.links)) {
      return financialTransactionsRead;
    }
    return (transaction.links ?? []).some(
      (link) => link.linkType !== TransactionLinkType.LEASE || visibleLeaseIds.has(link.linkId)
    );
  });
};
